<template>
  <Form
    :form-id="formId"
    :initial-values="initialValues"
    action="#"
    class="section section-row div-5 expense-row"
    :class="{ dirty }"
  >
    <HiddenInput input-id="id" />

    <div class="triple-f">
      <VPInput
        ref="nameRef"
        :disabled="disabled"
        input-id="name"
        :placeholder="'Add expense to ' + placeholderAddition"
      />
    </div>
    <div class="double-f">
      <VPPriceInput
        ref="amountRef"
        :disabled="disabled"
        input-id="amount"
        placeholder="Add amount"
        hide-empty-input
      />
    </div>
  </Form>
</template>

<script setup lang="ts">
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import VPInput from '@/components/form/VPInput.vue';
import VPPriceInput from '@/components/form/VPPriceInput.vue';

const formsStore = useFormsStore();

const dirty = computed(() => formsStore.isFormDirty(props.formId));

interface Props {
  formId: string;
  initialValues: unknown;
  disabled?: boolean;
  placeholderAddition: string;
}

const props = defineProps<Props>();

const nameRef = ref<VPInput>(null);
const amountRef = ref<VPPriceInput>(null);

const emit = defineEmits<{
  (e: 'blur'): void;
  (e: 'save'): void;
  (e: 'clear'): void;
}>();

const refs = [nameRef, amountRef];

const activeElement = ref<EventTarget | null>(null);

const focusChanged = (e: Event) => {
  activeElement.value = e.target;
};

onMounted(() => {
  document.addEventListener('focusin', focusChanged);
});
onBeforeUnmount(() => {
  document.removeEventListener('focusin', focusChanged);
});

const hasFocus = computed(() =>
  refs.some(r => r.value?.inputRef && activeElement.value && r.value?.inputRef === activeElement.value),
);

const hasValue = computed(() => {
  return ['name', 'amount'].some(f => formsStore.fieldGetValue(props.formId, f));
});

watch(hasFocus, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    if (hasValue.value) {
      emit('blur');
    } else {
      emit('clear');
    }
  }
});
</script>
